
export default function ZoningSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Understanding Land Zoning Types
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Zoning is one of the most important factors affecting your land's value and what can be built on it. Here's what you need to know about common zoning classifications in El Paso County.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-home-4-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">R1 - Single Family Residential</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> Single-family homes, typically one dwelling per lot. Minimum lot sizes usually 5,000-10,000 sq ft depending on subdivision.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Highest per-acre value for residential land. Most desirable for homebuilders and individual buyers.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $20,000-$50,000 per acre in established areas, higher for infill lots.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-building-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">R2/R3 - Multi-Family Residential</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> Duplexes, townhomes, apartments, and condos. Higher density than R1 zoning.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Can be more valuable than R1 due to development potential, but limited buyer pool (mostly developers).
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $30,000-$80,000 per acre depending on location and density allowed.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-store-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Commercial Zoning</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> Retail, offices, restaurants, and other businesses. Specific uses depend on zoning subcategory (C1, C2, C3).
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Highest value potential, especially on major roads. Very location-dependent.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $50,000-$200,000+ per acre on major corridors, less on secondary roads.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-plant-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Agricultural Zoning</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> Farming, ranching, livestock. Usually allows one home per 5-40 acres depending on jurisdiction.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Lowest per-acre value. Limited development potential keeps prices down.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $1,000-$5,000 per acre in rural areas, higher near city limits.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-factory-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Industrial Zoning</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> Manufacturing, warehousing, distribution centers. Heavy industrial allows more intensive uses.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Moderate to high value, but very location-specific. Needs highway access and utilities.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $15,000-$60,000 per acre near industrial corridors.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-lg mb-6">
              <i className="ri-landscape-line text-teal-600 text-3xl"></i>
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">Rural/Unzoned Land</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Allowed Uses:</strong> In unincorporated areas, uses may be unrestricted or follow county guidelines. Check with county planning.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Value Impact:</strong> Variable. Flexibility can be valuable, but lack of infrastructure limits development.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Typical Price:</strong> $1,500-$8,000 per acre depending on location and access.
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-8 shadow-lg mb-12">
          <h3 className="text-3xl font-bold mb-6 text-gray-900">How to Check Your Land's Zoning</h3>
          <div className="space-y-4 text-gray-700">
            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 font-bold">
                1
              </div>
              <div>
                <strong className="block mb-2">Check Your Property Tax Statement</strong>
                <p className="leading-relaxed">Your annual property tax statement usually lists the zoning classification. Look for codes like "R1," "AG," or "C2."</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 font-bold">
                2
              </div>
              <div>
                <strong className="block mb-2">Visit the County Assessor's Website</strong>
                <p className="leading-relaxed">El Paso County and Doña Ana County (NM) have online GIS systems where you can search by address or parcel number to view zoning maps.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 font-bold">
                3
              </div>
              <div>
                <strong className="block mb-2">Call the Planning Department</strong>
                <p className="leading-relaxed">If you can't find the information online, call your local planning or zoning department. They can tell you the current zoning and what uses are permitted.</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 text-white rounded-full flex-shrink-0 font-bold">
                4
              </div>
              <div>
                <strong className="block mb-2">Ask Us - We'll Research It for You</strong>
                <p className="leading-relaxed">When you contact TRex RE LLC for a cash offer, we'll research your property's zoning as part of our evaluation. You don't need to figure it out yourself.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-teal-50 border-l-4 border-teal-600 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">Can Zoning Be Changed?</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Yes, but it's complicated and expensive. Rezoning requires filing applications, attending public hearings, and often hiring attorneys and engineers. The process can take 6-12 months and cost $5,000-$20,000 with no guarantee of approval.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Most individual landowners don't pursue rezoning. Instead, they sell to developers or investors who have the resources to navigate the process. When you sell to TRex RE LLC, we buy your land in its current zoning - no rezoning required.
          </p>
        </div>
      </div>
    </section>
  );
}
